import { ChangeEvent, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, FormHelpText, FormCheckbox } from '@spotify-internal/encore-web';

import TextField from 'components/Fields/TextField/TextField';
import {
  withFullTranslationKey,
  CLAIM_DETAILS,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { getFieldValid, getFieldValue } from 'components/ContentReporting/form-utils';
import { FormData, FieldOnChangeEvent } from 'containers/ContentReporting/ContentLegalReporting/utils/types';

type TerritoriesFieldsProps = {
  formData: FormData;
  handleFieldChange: (fieldChangeEvent: FieldOnChangeEvent) => void;
  territoryList: string[];
  isOtherTerritorySelected: boolean;
  forceValidation: boolean;
};

const TerritoriesFields: FunctionComponent<TerritoriesFieldsProps> = props => {
  const { formData, handleFieldChange, territoryList, isOtherTerritorySelected, forceValidation } = props;

  const { t } = useTranslation('infringement_claim_form');

  const fieldValue = getFieldValue(formData);
  const fieldValid = getFieldValid(formData);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const currentTerritories = fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES);
    const newTerritories = {
      ...currentTerritories,
      [event.target.name]: event.target.checked,
    };

    handleFieldChange({
      fieldIdentifier: CLAIM_DETAILS.fields.CLAIM_TERRITORIES,
      value: newTerritories,
      valid: Object.values(newTerritories).some(x => !!x),
    });
  };

  return (
    <FormGroup key="territory-checkboxes" label={t('territories')} indicator="required">
      {territoryList.map(territory => (
        <FormCheckbox
          id={territory}
          key={territory}
          name={territory}
          onChange={handleOnChange}
          checked={
            !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES) &&
            !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES)[territory]
          }
        >
          {t(withFullTranslationKey(territory))}
        </FormCheckbox>
      ))}
      {!fieldValid(CLAIM_DETAILS.fields.CLAIM_TERRITORIES) && (
        <FormHelpText error>{t('territory_selection_required')}</FormHelpText>
      )}
      {isOtherTerritorySelected && (
        <TextField
          field={{
            name: CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES,
            type: 'text',
            required: true,
            maxlength: 244,
            labelOverride: t(CLAIM_DETAILS.labels.CLAIM_OTHER_TERRITORIES),
          }}
          value={fieldValue(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES)}
          valid={fieldValid(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES)}
          onFieldChange={handleFieldChange}
          forceValidation={forceValidation}
        />
      )}
    </FormGroup>
  );
};

export default TerritoriesFields;
